import { useEffect, useMemo, useState } from 'react';
import { Alert, Modal as AntModal } from 'antd';
import { useSelector, useDispatch } from 'react-redux';

import { usePost, useAuth, useGet, usePatch } from 'hooks';
import { Input, Select, Detail, Loader, Radio } from 'components/common';
import {
  sanityCheckAlert,
  userAlerts,
  allUsersIncludeInactive,
} from 'services/api';
import { getAlertToEdit } from 'selectors/alerts';
import { setAlertToEdit } from 'actions/alerts';
import {
  frequencyOptions,
  frequencyRadioOptions,
  frequencyEnums,
  frequencyRadioOptionsLimited,
} from 'models/frequency';
import licenseType from 'models/licenseType';
import { searchOrigin } from 'components/Search/Results/constants';
import Share from './Share';
import usePermissions from './usePermissions';
import { getListsFilters } from './helpers';

const EditModal = ({ onUpdateSuccess }) => {
  const dispatch = useDispatch();
  const activeAlert = useSelector(getAlertToEdit);
  const [showWarning, setShowWarning] = useState('');
  const [sharedUsers, setSharedUsers] = useState([]);
  const { user } = useAuth();

  const [{ res: success, loading: updating, error }, updateAlert] = usePatch({
    url: userAlerts,
  });

  const [
    { loading: sanityCheckLoading, error: sanityError },
    getUserAlerts,
  ] = usePost({
    url: sanityCheckAlert,
  });

  const [
    { res: allUsersInactiveRes, loading: allUsersInactiveLoading },
  ] = useGet({
    url: allUsersIncludeInactive,
    onMount: true,
  });

  const { checkUserPermissions } = usePermissions({
    activeAlert,
    userLicenses: user.licenses,
    allUsersRes: allUsersInactiveRes,
  });

  const userOptions = useMemo(
    () =>
      allUsersInactiveRes?.userAccounts
        .filter(u => u.active && u.legacyUserId !== user.userId)
        .map(u => ({
          fullname: u.fullname,
          licenses: u.licenses,
          userId: u.legacyUserId,
          missingPurchasingAlertLicense:
            activeAlert.searchOrigin === searchOrigin.purchasingSearch &&
            !u.licenses.some(l => l.type === licenseType.academic),
        }))
        .sort((a, b) => a.fullname.localeCompare(b.fullname)) || [],
    [allUsersInactiveRes, user.userId, activeAlert.searchOrigin]
  );

  useEffect(() => {
    const users = allUsersInactiveRes?.userAccounts
      .filter(u =>
        activeAlert.childAlert.users.some(
          u2 => u2.userId === u.legacyUserId && u.legacyUserId !== user.userId
        )
      )
      .map(u =>
        userOptions.some(x => x.userId === u.legacyUserId)
          ? u.legacyUserId
          : u.fullname
      );
    if (users?.length > 0) {
      handleUsersChange(users);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsersInactiveRes, userOptions, user.userId]);

  useEffect(() => {
    getUserAlerts({
      ...activeAlert,
      searchFilters: {
        ...getListsFilters(activeAlert, x => x),
      },
      nestedQueries: activeAlert.nestedQueries
        ? [...JSON.parse(activeAlert.nestedQueries)]
        : [],
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (success) {
      onUpdateSuccess();
    }
  }, [success, onUpdateSuccess, dispatch]);

  const handleEditChange = key => value => {
    const updatedAlert = {
      ...activeAlert,
      [key]: value,
    };

    dispatch(setAlertToEdit(updatedAlert));
  };

  const handleChildAlertChange = key => value => {
    const updatedAlert = {
      ...activeAlert,
      childAlert: {
        ...activeAlert.childAlert,
        [key]: value,
      },
    };

    dispatch(setAlertToEdit(updatedAlert));
  };

  const handleUsersChange = v => {
    const childAlertUsers = v.map(val =>
      typeof val === 'number'
        ? val
        : allUsersInactiveRes.userAccounts.find(y => y.fullname === val)
            .legacyUserId
    );
    handleChildAlertChange('users')(childAlertUsers);
    setSharedUsers(v);

    const warning = checkUserPermissions(childAlertUsers);
    setShowWarning(warning);
  };

  const handleCancel = () => {
    dispatch(setAlertToEdit());
  };

  const handleUpdate = () => {
    const frequency =
      activeAlert.frequencyRadio === 'Alert'
        ? frequencyOptions.find(freq => freq.value === activeAlert.frequency)
            .enumValue
        : frequencyEnums.NoAlert;

    let childAlert = {
      users: [],
      frequency: frequencyEnums.NoAlert,
    };
    if (activeAlert.childAlert.users.length) {
      const childFrequency =
        activeAlert.childAlert.frequencyRadio === 'Alert'
          ? frequencyOptions.find(
              freq => freq.value === activeAlert.childAlert.frequency
            ).enumValue
          : frequencyEnums.NoAlert;
      childAlert = {
        ...activeAlert.childAlert,
        frequency: childFrequency,
      };
    }

    updateAlert({
      ...activeAlert,
      name: activeAlert.name?.trim(),
      monthlyKeywords: null,
      nestedQueries: null,
      ...getListsFilters(activeAlert),
      frequency,
      childAlert,
    });
  };

  if (!activeAlert) return null;

  const isValid = activeAlert.name?.trim() && activeAlert.monthlyKeywords;

  return (
    <AntModal
      open
      title={
        user.disableExport
          ? 'Manage Searches'
          : 'Manage Saved Searches & Alerts'
      }
      onCancel={handleCancel}
      okText='Save'
      onOk={handleUpdate}
      okButtonProps={{
        disabled: updating || !isValid || sanityError,
        'data-testid': 'edit-save-button',
      }}
      cancelButtonProps={{
        disabled: updating,
        'data-testid': 'edit-cancel-button',
      }}
    >
      {sanityCheckLoading || allUsersInactiveLoading ? (
        <Loader />
      ) : sanityError ? (
        <div />
      ) : (
        <div>
          <Detail
            label={
              <div>
                Rename Saved Search <span style={{ color: 'red' }}>*</span>
              </div>
            }
          >
            <Input
              showCount
              disabled={updating}
              maxLength={50}
              value={activeAlert.name}
              onChange={handleEditChange('name')}
              placeholder='Type the name of your saved search or alert'
            />
          </Detail>
          {!user.disableExport && (
            <Detail style={{ marginTop: '21px', marginBottom: '21px' }}>
              <Radio
                radioStyle={{ height: '30px', lineHeight: '30px' }}
                value={activeAlert.frequencyRadio}
                onChange={handleEditChange('frequencyRadio')}
                options={
                  user.isPartnerAccount
                    ? frequencyRadioOptionsLimited
                    : frequencyRadioOptions
                }
              />
              <Select
                disabled={updating || activeAlert.frequencyRadio !== 'Alert'}
                value={activeAlert.frequency}
                onChange={handleEditChange('frequency')}
                options={frequencyOptions}
                disableTooltip
                style={{ minWidth: '95px' }}
              />
            </Detail>
          )}
          <Share
            userOptions={userOptions}
            activeAlert={activeAlert}
            sharedUsers={sharedUsers}
            handleUsersChange={handleUsersChange}
            showWarning={showWarning}
            loading={updating}
            handleChildAlertChange={handleChildAlertChange}
            disabled={updating}
          />
        </div>
      )}

      {sanityError && (
        <Alert
          message={'This is not a valid search and cannot be managed.'}
          type='error'
        />
      )}
      {error && <Alert message={error.message?.message ?? ''} type='error' />}
    </AntModal>
  );
};

export default EditModal;
